import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

// Redux
import { selectPatient } from "Store/PatientSlice";
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";
import { selectUserEmail } from "Store/AuthSlice";

// Material UI
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Stack,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

// Components
import { AuthFooter } from "Components/Authentication/Components/AuthFooter";
import { OTP } from "Components/Authentication/Components/OTP";

// Formik
import { useFormik } from "formik";
import * as yup from "yup";

// Resource
import {
  sendVerificationCode,
  sendVerificationCodeAdmin,
  verifyCode,
  verifyCodeAdmin,
} from "API/Resource/verify";

// Styles
import { styles } from "./styles";

// Constants
import { emeraldGreen, terracotta } from "Constants/ColourConstants";
import { TEN_MINUTES } from "Constants/TimeConstants";

// i18n
import { useTranslation } from "react-i18next";

// Validation schema
const MFA = ({ open, onClose, onVerify, isAdmin = false }) => {
  const { t } = useTranslation();

  const validationSchema = yup.object({
    code: yup
      .string(t("mfa.verificationCode"))
      .length(6, t("mfa.6DigitsCode"))
      .required(t("mfa.codeRequired")),
  });

  const patient = useSelector(selectPatient);
  const userEmail = useSelector(selectUserEmail);

  const dispatch = useDispatch();

  const email = isAdmin ? userEmail : patient?.maskedEmail;
  const number = isAdmin ? null : patient?.maskedPrimaryPhone;

  const [step, setStep] = useState(isAdmin ? 2 : 1);
  const [method, setMethod] = useState(number ? "PHONE" : "EMAIL");
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);

  const handleMFAError = useCallback(
    (error) => {
      const errorMessages = {
        INVALID_VERIFICATION: t("mfa.invalidCode"),
        MAX_ATTEMPTS_REACHED: t("errors.tooManyAttempts"),
      };

      const errorMessage = errorMessages[error.message] || error.message;
      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    },
    [dispatch, t]
  );

  const handleSendCodeAdmin = useCallback(async () => {
    try {
      setIsSendingCode(true);
      await sendVerificationCodeAdmin(userEmail);
      sessionStorage.setItem("adminMFA", "sent");
    } catch (error) {
      handleMFAError(error);
    } finally {
      setIsSendingCode(false);
    }
  }, [handleMFAError, userEmail]);

  const initAdminMFA = useCallback(async () => {
    if (sessionStorage.getItem("adminMFA") !== "sent") {
      handleSendCodeAdmin();
    }
    setMethod("EMAIL");
    setStep(2);
  }, [handleSendCodeAdmin]);

  const initPatientMFA = useCallback(() => {
    if (sessionStorage.getItem(`2FA${patient.id}`) === "sent") {
      setStep(2);
    } else {
      setStep(1);
    }
  }, [patient.id]);

  // Send verification code logic
  const handleSendCodePatient = async () => {
    try {
      setIsSendingCode(true);
      await sendVerificationCode(patient.id, method);
      dispatch(
        openSnackbar({
          message: t("mfa.codeSent"),
          severity: "success",
        })
      );
      sessionStorage.setItem(`2FA${patient.id}`, "sent");
      setTimeout(() => {
        if (sessionStorage.getItem(`2FA${patient.id}`) === "sent") {
          sessionStorage.removeItem(`2FA${patient.id}`);
          setStep(1);
        }
      }, TEN_MINUTES);
      setStep(2); // Proceed to enter verification code
    } catch (error) {
      handleMFAError(error);
    } finally {
      setIsSendingCode(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    if (isAdmin) {
      await handleSendCodeAdmin();
    } else {
      await handleSendCodePatient();
    }
  };

  const handleVerifyCodeAdmin = async (code) => {
    await verifyCodeAdmin(userEmail, code);
    sessionStorage.setItem("adminMFA", "verified");
  };

  const handleVerifyCodePatient = async (code) => {
    await verifyCode(patient.id, method, code);
    sessionStorage.setItem(`2FA${patient.id}`, "verified");
  };

  // Verify the code entered by the user
  const handleVerifyCode = async (values) => {
    try {
      setIsVerifyingCode(true);
      if (isAdmin) {
        await handleVerifyCodeAdmin(values.code);
      } else {
        await handleVerifyCodePatient(values.code);
      }
      dispatch(
        openSnackbar({
          message: t("mfa.verificationSuccess"),
          severity: "success",
        })
      );
      onVerify();
    } catch (error) {
      handleMFAError(error);
    } finally {
      setIsVerifyingCode(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      initAdminMFA();
    } else {
      initPatientMFA();
    }
  }, [isAdmin, initAdminMFA, initPatientMFA]);

  const MethodContent = () => {
    return (
      <>
        <DialogContent sx={styles.dialogContent}>
          <DialogContentText sx={styles.dialogContentText}>
            {number ? t("mfa.selectMethod") : t("mfa.emailMethod")}
          </DialogContentText>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            sx={styles.radioGroup}
            value={method}
            name="radio-buttons-group"
            onChange={(e) => setMethod(e.target.value)}
          >
            {number && (
              <FormControlLabel
                value="PHONE"
                control={
                  <Radio
                    sx={{
                      "&.Mui-checked": {
                        color: terracotta,
                      },
                    }}
                  />
                }
                label={`${t("mfa.textTo")} ${number}`}
              />
            )}
            <FormControlLabel
              value="EMAIL"
              control={
                <Radio
                  sx={{
                    "&.Mui-checked": {
                      color: terracotta,
                    },
                  }}
                />
              }
              label={`${t("mfa.emailTo")} ${email}`}
            />
          </RadioGroup>
          <AuthFooter showCopyright={false} />
        </DialogContent>
        <Stack sx={styles.mfaButtons}>
          <Button variant="text" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={styles.submitButton}
            onClick={handleSendCodePatient}
            disabled={isSendingCode}
          >
            {isSendingCode ? t("buttons.sending") : t("buttons.continue")}
          </Button>
        </Stack>
      </>
    );
  };

  const CodeContent = () => {
    const contentText = `${t("mfa.enterCode01")} ${
      method === "EMAIL" ? email : number
    }. ${t("mfa.enterCode02")}`;

    const formik = useFormik({
      initialValues: {
        code: "",
      },
      validationSchema,
      onSubmit: (values) => {
        handleVerifyCode(values);
      },
    });

    return (
      <>
        <DialogContent sx={styles.dialogContent}>
          <DialogContentText sx={styles.dialogContentText}>
            {contentText}
          </DialogContentText>
          <OTP
            setCode={formik.setFieldValue}
            resendCode={handleResendCode}
            values={formik.values}
            handleChange={formik.handleChange("code")}
            errors={formik.errors}
            touched={formik.touched}
          />
          <AuthFooter showCopyright={false} />
        </DialogContent>
        <Stack sx={styles.mfaButtons}>
          <Button variant="text" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={styles.submitButton}
            onClick={formik.handleSubmit}
            disabled={isVerifyingCode}
          >
            {t("buttons.submit")}
          </Button>
        </Stack>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isAdmin ? null : onClose}
      sx={styles.dialogPaper}
    >
      <DialogTitle color={emeraldGreen} sx={styles.dialogTitle}>
        {step === 2 && !isAdmin && (
          <Button
            variant="contained"
            sx={styles.backButton}
            onClick={() => setStep(1)}
          >
            <ArrowBack sx={styles.backArrow} />
            {t("buttons.back")}
          </Button>
        )}
        {t("mfa.securityVerification")}
      </DialogTitle>
      {step === 1 ? <MethodContent /> : <CodeContent />}
    </Dialog>
  );
};

export default MFA;
